import React, { useState, useRef, useEffect, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { Stack, Box, Typography, Button } from '@mui/material';
import { OpenEndedAssistantController } from 'controllers/openEndedAssistant';
import { TranscriptionControllerFactory } from 'controllers/transcription/transcriptionControllerFactory';
import { TranscriptionController, TranscriptionMessage } from 'controllers/transcription/types';
import { UserContext } from 'components/UserContext';
import { ConversationRole } from 'enums/conversationRole';
import { Row } from 'components/Row/Row';
import { ConversationItem } from 'controllers/types';
import { ArrowForward } from '@mui/icons-material';
import { OpenEndedMicButton } from './OpenEndedMicButton';
import { OpenEndedTextInput } from './OpenEndedTextInput';
import { ConversationTurn } from './ConversationTurn';

export const ROLE_USER = 'You';

type OpenEndedProps = {
  chatId: number;
  handleOpenEnded: (status: string) => void;
  question: string | null;
};

export const OpenEnded = ({ chatId, handleOpenEnded, question }: OpenEndedProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useContext(UserContext);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [chatStatus, setChatStatus] = useState('');
  const [isAssistantResponding, setIsAssistantResponding] = useState(false);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);

  const transcriptionControllerRef = useRef<TranscriptionController | null>(null);

  const [finalizedTranscriptPortion, setFinalizedTranscriptPortion] = useState('');
  const [partialTranscriptPortion, setPartialTranscriptPortion] = useState('');

  const lastTranscriptPortionStartRef = useRef(0);

  const hasReceivedNewTranscriptSinceStudentMessageRef = useRef(true);

  const [micButtonMuted, setMicButtonMuted] = useState(true);
  const [isTranscriptInitializationInProgress, setIsTranscriptInitializationInProgress] = useState(true);

  const addConversationMessage = (text: string, role = ConversationRole.AI) => {
    setConversation(prevConvo => [...prevConvo, { role, text }]);
  };

  const appendToLastMessage = (text: string) => {
    setConversation(prevConvo => {
      const lastMessage = prevConvo[prevConvo.length - 1];
      lastMessage.text += text;
      return [...prevConvo];
    });
  };

  const replaceLastMessage = (text: string) => {
    setConversation(prevConvo => {
      const lastMessage = prevConvo[prevConvo.length - 1];
      lastMessage.text = text;
      return [...prevConvo];
    });
  };

  const assistantControllerRef = useRef<OpenEndedAssistantController | null>(null);

  // Both playing received synthesized voice audio chunks,
  // and recording the user's voice + sending it to Speechmatics + handling the received transcription
  const stopAllVoiceMedia = () => {
    // Stop playing the synthesized voice audio chunks
    if (assistantControllerRef.current) assistantControllerRef.current.stop();

    // Stop sending the recorded audio and close session
    if (!transcriptionControllerRef.current) return;
    transcriptionControllerRef.current.pause();
    transcriptionControllerRef.current.stop();
  };

  useEffect(() => {
    assistantControllerRef.current = new OpenEndedAssistantController(
      chatId,
      appendToLastMessage,
      setIsAssistantResponding,
      enqueueSnackbar,
      setChatStatus,
    );
    return stopAllVoiceMedia; // On unmount, cleanup streams
  }, [chatId]); // eslint-disable-line

  // Reset the conversation when the chatId changes
  useEffect(() => {
    setConversation([]);
    setChatStatus('');
    setIsAssistantResponding(false);
    setMicButtonMuted(true);
    setIsTranscriptInitializationInProgress(true);
    setPartialTranscriptPortion('');
    setFinalizedTranscriptPortion('');
    lastTranscriptPortionStartRef.current = 0;
    hasReceivedNewTranscriptSinceStudentMessageRef.current = true;
  }, [chatId]);

  // This function takes an input string as the current conversation turn.
  const submitResponse = (chat: string) => {
    if (isAssistantResponding) return;
    if (!chat) return;

    setConversation(prevConvo => [...prevConvo, { role: user?.first_name || ROLE_USER, text: chat }]);
    setIsAssistantResponding(true);
    hasReceivedNewTranscriptSinceStudentMessageRef.current = false;

    // Add an empty message to the conversation from the assistant,
    // to be filled in by the assistant's streaming response.
    addConversationMessage('');
    if (assistantControllerRef.current) {
      assistantControllerRef.current.stop(); // If the user is trying to interrupt the assistant, stop the current audio playback
      assistantControllerRef.current.streamResponse(chat);
    }
  };

  // When the user submits their question, we want to stop transcribing audio
  // When the AI finishes replying, we want to start transcribing again
  useEffect(() => {
    if (transcriptionControllerRef.current)
      transcriptionControllerRef.current.pause(isAssistantResponding || micButtonMuted);
    setPartialTranscriptPortion('');
    setFinalizedTranscriptPortion('');
  }, [isAssistantResponding, micButtonMuted]);

  const shouldIgnoreTranscript = (message: TranscriptionMessage) => {
    const start = message.metadata.start_time;
    if (!hasReceivedNewTranscriptSinceStudentMessageRef.current && start === lastTranscriptPortionStartRef.current) {
      console.warn(`Received partial transcript from before student message - ignoring:${message.metadata.transcript}`);
      // This audio was sent to Speechmatics before the student sent their message, so we can ignore it
      console.debug('ignoring');
      return true; // Should return from parent context
    }
    console.debug('not ignoring');
    return false; // Should continue with parent context
  };

  const handlePartialTranscript = (message: TranscriptionMessage) => {
    console.debug('handlePartial: ', message);
    if (shouldIgnoreTranscript(message)) return;

    const partialTranscript = message.metadata.transcript;
    setPartialTranscriptPortion(partialTranscript);
  };

  const handleFinalTranscript = (message: TranscriptionMessage) => {
    console.debug('handleFinal: ', message);
    if (shouldIgnoreTranscript(message)) {
      hasReceivedNewTranscriptSinceStudentMessageRef.current = true;
      lastTranscriptPortionStartRef.current = message.metadata.start_time;
      return;
    }

    // When we receive a finalized transcript, we need to shift it to the left into the finalized portion of the state,
    // and clear the partial portion.
    const finalizedSegment = message.metadata.transcript;
    setFinalizedTranscriptPortion(prevTranscript => prevTranscript + finalizedSegment);
    setPartialTranscriptPortion('');
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  const weAreDone = () =>
    chatStatus === 'done_finished' || chatStatus === 'done_max_turns' || chatStatus === 'done_moderation';

  const handleMicButtonClick = (muted: boolean) => {
    console.debug('handeMicButtonClick: ', muted);
    setMicButtonMuted(muted);
    if (!transcriptionControllerRef.current) {
      // If we're clicking for the first time, we need to initialize the controller
      transcriptionControllerRef.current = TranscriptionControllerFactory.create(
        handlePartialTranscript,
        handleFinalTranscript,
      );
      setIsTranscriptInitializationInProgress(false);
      transcriptionControllerRef.current.initiate(() => setIsTranscriptInitializationInProgress(true));
    }

    if (muted) {
      transcriptionControllerRef.current
        .awaitTranscription()
        .then((transcription: string | void) => {
          if (transcription) {
            // this is where we submit the text if using Whisper
            console.debug('trans: ', transcription);
            submitResponse(transcription);
          } else {
            // this is where we submit the text if using Speechmatics
            console.debug('not trans: ', finalizedTranscriptPortion, '//', partialTranscriptPortion);
            submitResponse(`${finalizedTranscriptPortion}${partialTranscriptPortion}`);
            setPartialTranscriptPortion('');
            setFinalizedTranscriptPortion('');
          }
        })
        .catch((err: Error) => {
          console.error(err);
        });
    } else {
      // They clicked the button to start recording
      // Reset the recorder so we don't include any audio from before they clicked unmute
      console.debug('resetting transcription');
      transcriptionControllerRef.current.reset?.();
      setPartialTranscriptPortion('');
      setFinalizedTranscriptPortion('');
    }
  };

  function isLastTurn(turnIndex: number, conversationLength: number) {
    return turnIndex === conversationLength - 1;
  }

  const questionTurn = { role: ConversationRole.AI, text: question || '' };

  return (
    <Stack gap={2} height='100%'>
      <Box minHeight={0} height='100%' overflow='auto'>
        <Stack gap={2}>
          {/* Treat the question as if it were the first turn in the conversation */}
          <ConversationTurn turn={questionTurn} />

          {conversation.map((turn: ConversationItem, idx: number) => (
            <ConversationTurn
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              turn={turn}
              isLastTurn={isLastTurn(idx, conversation.length)}
              ref={isLastTurn(idx, conversation.length) ? scrollRef : null}
              chatId={chatId}
              replaceLastMessage={replaceLastMessage}
              question={question}
              conversation={conversation}
            />
          ))}
        </Stack>
      </Box>
      <Box display='flex' justifyContent='center'>
        {weAreDone() ? (
          <Button onClick={() => handleOpenEnded(chatStatus)} endIcon={<ArrowForward />}>
            Continue
          </Button>
        ) : (
          <Row flex={1} gap={1}>
            <OpenEndedMicButton
              isTranscriptInitializationInProgress={isTranscriptInitializationInProgress}
              micButtonMuted={micButtonMuted}
              setMicButtonMuted={handleMicButtonClick}
            />
            <OpenEndedTextInput micButtonMuted={micButtonMuted} handleSend={submitResponse} />
          </Row>
        )}
      </Box>
      <Typography variant='bodySmall' textAlign='center' color='text.secondary'>
        All responses are generated by AI.
      </Typography>
    </Stack>
  );
};
